// 资产索引（EP-003-S1）—— 13 + 12 = 25 个 AssetId → 运行时 URL 路径。
// 路径由 art/manifest.json 的 `category` + `id` 推导，**不手抄任何路径字符串**（违反 ADR 即重写风险）。
// 拷贝脚本 scripts/copy-assets.mjs 按同样的映射把锚图从 art/ref 落到 public/assets 下，
// 故这里只用「逻辑路径」，与磁盘文件名保持单一来源。
import manifest from '../../art/manifest.json';

type AssetCategory = 'scene' | 'yard' | 'character' | 'gift' | 'postcard';

const SUBDIR: Record<AssetCategory, string> = {
  scene: 'scenes/room',
  yard: 'scenes/yard', // 🆕 v2.2：庭院底图（scene_yard_day，1024×1536）
  character: 'characters/dog',
  gift: 'items/gifts',
  postcard: 'postcards', // 明信片 4:3 横构图
};

// 🆕 v2.1：明信片按 noteId 独立（12 张，每张对应一条文案）。
//   noteId 直接作为 assetId（如 postcard_riverside_01），与 POSTCARD_NOTES[i].id 一一对应。
//   运行时按 entry.noteId 查 assetIndex，无则降级占位灰块。
const POSTCARD_NOTE_IDS = [
  'postcard_riverside_01', 'postcard_riverside_02', 'postcard_riverside_03',
  'postcard_forest_01',   'postcard_forest_02',   'postcard_forest_03',
  'postcard_hill_01',     'postcard_hill_02',     'postcard_hill_03',
  'postcard_town_01',     'postcard_town_02',     'postcard_town_03',
] as const;

// 🆕 v2：AssetId 联合类型 = manifest 资产 + 12 个明信片。
export type AssetId =
  | (typeof manifest.assets)[number]['id']
  | (typeof POSTCARD_NOTE_IDS)[number];

function buildPath(category: AssetCategory, id: string): string {
  // 🆕 WebP 转换管线已落地（art-polish §1.4-C1/C2/C3 + /tmp 转换脚本）：25 张锚图全部转 .webp
  // （PNG 留 art/ref、art/raw 作源，public/assets 现只存 .webp；总量 31.9MB→1.37MB）。
  return `/assets/${SUBDIR[category]}/${id}@2x.webp`;
}

export const assetIndex: Record<AssetId, string> = {
  ...(Object.fromEntries(
    manifest.assets.map((a) => [a.id, buildPath(a.category as AssetCategory, a.id)]),
  ) as Record<(typeof manifest.assets)[number]['id'], string>),
  // 🆕 v2.1：12 张明信片（按 noteId，文件已落地 public/assets/postcards/）
  ...Object.fromEntries(
    POSTCARD_NOTE_IDS.map((id) => [id, buildPath('postcard', id)]),
  ) as Record<(typeof POSTCARD_NOTE_IDS)[number], string>,
};

// 🆕 v2.1：noteId → AssetId（noteId 直接就是 assetId）。
//   PostcardDetail 用 entry.noteId 查本映射，再查 assetIndex 得 URL。
export const POSTCARD_ASSET_BY_NOTE: Readonly<Record<string, AssetId>> = Object.fromEntries(
  POSTCARD_NOTE_IDS.map((id) => [id, id]),
);

// 🆕 v2（保留，兼容旧代码）：DestinationId → 单张占位 AssetId。
//   旧逻辑（按 destination 取图）现已废弃——每张明信片有独立图片，不再共享。
import type { DestinationId } from '../save/types';
export const POSTCARD_ASSET_BY_DEST: Readonly<Record<DestinationId, AssetId>> = {
  riverside: 'postcard_riverside_01', // 降级fallback；真实渲染走 POSTCARD_ASSET_BY_NOTE[entry.noteId]
  forest:    'postcard_forest_01',
  hill:      'postcard_hill_01',
  town:      'postcard_town_01',
};
